/**
 * Delivery Slots Settings — Admin Dashboard
 * Manage default delivery slots + date-range overrides for scheduled deliveries.
 */

import React, { useState, useEffect } from 'react';
import {
  Clock,
  Plus,
  Trash2,
  ToggleLeft,
  ToggleRight,
  AlertCircle,
  CheckCircle,
  Loader2,
  RefreshCw,
  Edit2,
  Save,
  X,
  Calendar,
} from 'lucide-react';
import deliverySlotService from '../../services/deliverySlotService';
import ScheduledListAlerts from './ScheduledListAlerts';

const formatTimeAmPm = (hhmm) => {
  if (!hhmm) return '';
  const [hStr, mStr] = hhmm.split(':');
  let h = parseInt(hStr, 10);
  const m = parseInt(mStr || '0', 10);
  const period = h >= 12 ? 'pm' : 'am';
  h = h % 12 || 12;
  return m === 0 ? `${h}${period}` : `${h}:${String(m).padStart(2, '0')}${period}`;
};

const toHHMM = (t) => (t ? t.slice(0, 5) : '');  // "07:00:00" -> "07:00"

const DeliverySlotsSettings = () => {
  const [slots, setSlots] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // New slot form
  const [newLabel, setNewLabel] = useState('');
  const [newStart, setNewStart] = useState('07:00');
  const [newEnd, setNewEnd] = useState('10:00');
  const [newCutoff, setNewCutoff] = useState('6');
  const [newSort, setNewSort] = useState('0');
  const [adding, setAdding] = useState(false);

  // Edit state for slots
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState('');
  const [editStart, setEditStart] = useState('');
  const [editEnd, setEditEnd] = useState('');
  const [editCutoff, setEditCutoff] = useState('');
  const [editSort, setEditSort] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // New override form
  const [newOvStart, setNewOvStart] = useState('');
  const [newOvEnd, setNewOvEnd] = useState('');
  const [newOvSlotIds, setNewOvSlotIds] = useState([]);   // selected ids
  const [newOvNote, setNewOvNote] = useState('');
  const [addingOv, setAddingOv] = useState(false);

  // Edit state for overrides
  const [editingOvId, setEditingOvId] = useState(null);
  const [editOvStart, setEditOvStart] = useState('');
  const [editOvEnd, setEditOvEnd] = useState('');
  const [editOvSlotIds, setEditOvSlotIds] = useState([]);
  const [editOvNote, setEditOvNote] = useState('');
  const [savingOvId, setSavingOvId] = useState(null);
  const [deletingOvId, setDeletingOvId] = useState(null);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    const [slotsRes, overridesRes] = await Promise.all([
      deliverySlotService.listAllSlots(),
      deliverySlotService.listAllOverrides(),
    ]);
    if (slotsRes.success) setSlots(slotsRes.data);
    else setError(slotsRes.error);
    if (overridesRes.success) setOverrides(overridesRes.data);
    else if (!slotsRes.success === false) setError(overridesRes.error);
    setLoading(false);
  };

  const showSuccess = (msg) => {
    setSuccess(msg);
    setError('');
    setTimeout(() => setSuccess(''), 3000);
  };

  // ── Slot handlers ────────────────────────────────────────────────────────

  const handleAddSlot = async () => {
    setError('');
    if (!newLabel.trim()) { setError('Label is required'); return; }
    if (newStart >= newEnd) { setError('Start time must be before end time'); return; }
    setAdding(true);
    const result = await deliverySlotService.createSlot({
      label: newLabel.trim(),
      startTime: newStart,
      endTime: newEnd,
      cutoffLeadHours: parseInt(newCutoff, 10) || 0,
      sortOrder: parseInt(newSort, 10) || 0,
    });
    if (result.success) {
      setSlots(prev => [...prev, result.data].sort((a, b) => (a.sortOrder - b.sortOrder) || a.startTime.localeCompare(b.startTime)));
      setNewLabel('');
      setNewStart('07:00');
      setNewEnd('10:00');
      setNewCutoff('6');
      setNewSort('0');
      showSuccess(`Slot "${result.data.label}" added`);
    } else {
      setError(result.error);
    }
    setAdding(false);
  };

  const startEditSlot = (s) => {
    setEditingId(s.id);
    setEditLabel(s.label);
    setEditStart(toHHMM(s.startTime));
    setEditEnd(toHHMM(s.endTime));
    setEditCutoff(String(s.cutoffLeadHours));
    setEditSort(String(s.sortOrder));
  };

  const cancelEditSlot = () => {
    setEditingId(null);
  };

  const handleSaveSlot = async (id) => {
    if (editStart >= editEnd) { setError('Start time must be before end time'); return; }
    setSavingId(id);
    const result = await deliverySlotService.updateSlot(id, {
      label: editLabel.trim(),
      startTime: editStart,
      endTime: editEnd,
      cutoffLeadHours: parseInt(editCutoff, 10) || 0,
      sortOrder: parseInt(editSort, 10) || 0,
    });
    if (result.success) {
      setSlots(prev => prev.map(s => s.id === id ? result.data : s).sort((a, b) => (a.sortOrder - b.sortOrder) || a.startTime.localeCompare(b.startTime)));
      showSuccess('Slot updated');
      setEditingId(null);
    } else {
      setError(result.error);
    }
    setSavingId(null);
  };

  const handleToggleSlot = async (s) => {
    setTogglingId(s.id);
    const result = await deliverySlotService.updateSlot(s.id, { isActive: !s.isActive });
    if (result.success) {
      setSlots(prev => prev.map(x => x.id === s.id ? result.data : x));
      showSuccess(`Slot ${result.data.isActive ? 'activated' : 'deactivated'}`);
    } else {
      setError(result.error);
    }
    setTogglingId(null);
  };

  const handleDeleteSlot = async (s) => {
    if (!window.confirm(`Permanently delete slot "${s.label}"? This cannot be undone. (Tip: use the toggle to disable instead.)`)) return;
    setDeletingId(s.id);
    const result = await deliverySlotService.deleteSlot(s.id);
    if (result.success) {
      setSlots(prev => prev.filter(x => x.id !== s.id));
      showSuccess(`Slot "${s.label}" removed`);
    } else {
      setError(result.error);
    }
    setDeletingId(null);
  };

  // ── Override handlers ────────────────────────────────────────────────────

  const handleAddOverride = async () => {
    setError('');
    if (!newOvStart || !newOvEnd) { setError('Start and end dates are required'); return; }
    if (newOvStart > newOvEnd) { setError('Start date must be on or before end date'); return; }
    setAddingOv(true);
    const result = await deliverySlotService.createOverride({
      startDate: newOvStart,
      endDate: newOvEnd,
      slotIds: newOvSlotIds,
      note: newOvNote.trim() || undefined,
    });
    if (result.success) {
      setOverrides(prev => [...prev, result.data].sort((a, b) => a.startDate.localeCompare(b.startDate)));
      setNewOvStart('');
      setNewOvEnd('');
      setNewOvSlotIds([]);
      setNewOvNote('');
      showSuccess('Override added');
    } else {
      setError(result.error);
    }
    setAddingOv(false);
  };

  const startEditOverride = (ov) => {
    setEditingOvId(ov.id);
    setEditOvStart(ov.startDate);
    setEditOvEnd(ov.endDate);
    setEditOvSlotIds(Array.isArray(ov.slotIds) ? ov.slotIds : []);
    setEditOvNote(ov.note || '');
  };

  const cancelEditOverride = () => {
    setEditingOvId(null);
  };

  const handleSaveOverride = async (id) => {
    if (editOvStart > editOvEnd) { setError('Start date must be on or before end date'); return; }
    setSavingOvId(id);
    const result = await deliverySlotService.updateOverride(id, {
      startDate: editOvStart,
      endDate: editOvEnd,
      slotIds: editOvSlotIds,
      note: editOvNote.trim() || null,
    });
    if (result.success) {
      setOverrides(prev => prev.map(o => o.id === id ? result.data : o).sort((a, b) => a.startDate.localeCompare(b.startDate)));
      showSuccess('Override updated');
      setEditingOvId(null);
    } else {
      setError(result.error);
    }
    setSavingOvId(null);
  };

  const handleDeleteOverride = async (ov) => {
    if (!window.confirm(`Delete this override for ${ov.startDate} → ${ov.endDate}?`)) return;
    setDeletingOvId(ov.id);
    const result = await deliverySlotService.deleteOverride(ov.id);
    if (result.success) {
      setOverrides(prev => prev.filter(o => o.id !== ov.id));
      showSuccess('Override removed');
    } else {
      setError(result.error);
    }
    setDeletingOvId(null);
  };

  // ── Render helpers ───────────────────────────────────────────────────────

  const renderSlotMultiSelect = (selectedIds, onChange) => (
    <div className="flex flex-wrap gap-2">
      {slots.length === 0 ? (
        <span className="text-xs text-gray-400 italic">Add slots first</span>
      ) : (
        slots.map(s => {
          const checked = selectedIds.includes(s.id);
          return (
            <label
              key={s.id}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs border cursor-pointer transition-colors ${
                checked
                  ? 'bg-[#548B2E] text-white border-[#548B2E]'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-[#548B2E]'
              }`}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => {
                  const next = e.target.checked
                    ? [...selectedIds, s.id]
                    : selectedIds.filter(x => x !== s.id);
                  onChange(next);
                }}
                className="sr-only"
              />
              {s.label} <span className="opacity-75">{formatTimeAmPm(toHHMM(s.startTime))}–{formatTimeAmPm(toHHMM(s.endTime))}</span>
            </label>
          );
        })
      )}
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Delivery Slots</h1>
        <p className="text-gray-600 text-sm sm:text-base">
          Manage time slots shown to customers on scheduled checkout. Use overrides for festival hours or one-off changes.
        </p>
      </div>

      {/* Scheduled slot pick-lists ready to download */}
      <ScheduledListAlerts />

      {/* Feedback */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700">
          <CheckCircle className="w-4 h-4 flex-shrink-0" />
          {success}
        </div>
      )}

      {/* ── Default slots ─────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm mb-6">
        <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5 text-[#548B2E]" />
          Default Slots
        </h2>

        {/* Add slot */}
        <div className="grid grid-cols-2 sm:grid-cols-6 gap-3 mb-5 bg-gray-50 p-3 rounded-lg">
          <div className="col-span-2 sm:col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">Label *</label>
            <input
              type="text"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder="Morning"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#548B2E] focus:border-transparent text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Start *</label>
            <input
              type="time"
              value={newStart}
              onChange={(e) => setNewStart(e.target.value)}
              className="w-full px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#548B2E] text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">End *</label>
            <input
              type="time"
              value={newEnd}
              onChange={(e) => setNewEnd(e.target.value)}
              className="w-full px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#548B2E] text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1" title="Stop taking orders this many hours before the slot's start time">Cutoff lead (hrs) *</label>
            <input
              type="number"
              min="0"
              max="720"
              value={newCutoff}
              onChange={(e) => setNewCutoff(e.target.value)}
              className="w-full px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#548B2E] text-sm"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={handleAddSlot}
              disabled={adding}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-[#548B2E] text-white rounded-lg hover:bg-[#3f6b20] transition-colors disabled:opacity-50 text-sm"
            >
              {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Add
            </button>
          </div>
        </div>

        {/* Slot list */}
        {loading ? (
          <div className="p-8 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#548B2E] mx-auto mb-2" />
            <p className="text-gray-500 text-sm">Loading…</p>
          </div>
        ) : slots.length === 0 ? (
          <div className="p-6 text-center bg-gray-50 rounded-lg">
            <Clock className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500 text-sm">No slots defined yet. Add your first slot above.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px]">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-600">Label</th>
                  <th className="text-center px-3 py-2 text-xs font-medium text-gray-600">Window</th>
                  <th className="text-center px-3 py-2 text-xs font-medium text-gray-600" title="Hours before the slot's start time that orders close">Cutoff lead</th>
                  <th className="text-center px-3 py-2 text-xs font-medium text-gray-600">Order</th>
                  <th className="text-center px-3 py-2 text-xs font-medium text-gray-600">Status</th>
                  <th className="text-right px-3 py-2 text-xs font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {slots.map((s) => (
                  <tr key={s.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2">
                      {editingId === s.id ? (
                        <input
                          type="text"
                          value={editLabel}
                          onChange={(e) => setEditLabel(e.target.value)}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-[#548B2E]"
                        />
                      ) : (
                        <span className="font-medium text-gray-800">{s.label}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {editingId === s.id ? (
                        <div className="flex items-center justify-center gap-1">
                          <input
                            type="time"
                            value={editStart}
                            onChange={(e) => setEditStart(e.target.value)}
                            className="px-1 py-1 border border-gray-300 rounded text-xs"
                          />
                          <span className="text-gray-400">–</span>
                          <input
                            type="time"
                            value={editEnd}
                            onChange={(e) => setEditEnd(e.target.value)}
                            className="px-1 py-1 border border-gray-300 rounded text-xs"
                          />
                        </div>
                      ) : (
                        <span className="text-gray-700 text-sm">
                          {formatTimeAmPm(toHHMM(s.startTime))} – {formatTimeAmPm(toHHMM(s.endTime))}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {editingId === s.id ? (
                        <input
                          type="number"
                          min="0"
                          max="720"
                          value={editCutoff}
                          onChange={(e) => setEditCutoff(e.target.value)}
                          className="w-14 px-1 py-1 border border-gray-300 rounded text-center text-sm"
                        />
                      ) : (
                        <span className="text-gray-700 text-sm">{s.cutoffLeadHours}h before</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {editingId === s.id ? (
                        <input
                          type="number"
                          value={editSort}
                          onChange={(e) => setEditSort(e.target.value)}
                          className="w-14 px-1 py-1 border border-gray-300 rounded text-center text-sm"
                        />
                      ) : (
                        <span className="text-gray-500 text-sm">{s.sortOrder}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                        s.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${s.isActive ? 'bg-green-500' : 'bg-red-500'}`} />
                        {s.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-1">
                        {editingId === s.id ? (
                          <>
                            <button
                              onClick={() => handleSaveSlot(s.id)}
                              disabled={savingId === s.id}
                              className="p-1.5 hover:bg-green-50 rounded-lg transition-colors"
                              title="Save"
                            >
                              {savingId === s.id ? <Loader2 className="w-4 h-4 animate-spin text-green-600" /> : <Save className="w-4 h-4 text-green-600" />}
                            </button>
                            <button
                              onClick={cancelEditSlot}
                              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                              title="Cancel"
                            >
                              <X className="w-4 h-4 text-gray-500" />
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => startEditSlot(s)}
                            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4 text-gray-500" />
                          </button>
                        )}
                        <button
                          onClick={() => handleToggleSlot(s)}
                          disabled={togglingId === s.id}
                          className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                          title={s.isActive ? 'Deactivate' : 'Activate'}
                        >
                          {togglingId === s.id ? (
                            <Loader2 className="w-4 h-4 animate-spin text-gray-500" />
                          ) : s.isActive ? (
                            <ToggleRight className="w-5 h-5 text-green-600" />
                          ) : (
                            <ToggleLeft className="w-5 h-5 text-gray-400" />
                          )}
                        </button>
                        <button
                          onClick={() => handleDeleteSlot(s)}
                          disabled={deletingId === s.id}
                          className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete permanently"
                        >
                          {deletingId === s.id ? (
                            <Loader2 className="w-4 h-4 animate-spin text-red-400" />
                          ) : (
                            <Trash2 className="w-4 h-4 text-red-400 hover:text-red-600" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Date overrides ────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#548B2E]" />
            Date Overrides
          </h2>
          <button
            onClick={fetchAll}
            className="p-2 text-gray-500 hover:text-[#548B2E] hover:bg-gray-50 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        {/* Add override */}
        <div className="grid grid-cols-1 sm:grid-cols-6 gap-3 mb-5 bg-gray-50 p-3 rounded-lg">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Start date *</label>
            <input
              type="date"
              value={newOvStart}
              onChange={(e) => setNewOvStart(e.target.value)}
              className="w-full px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#548B2E] text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">End date *</label>
            <input
              type="date"
              value={newOvEnd}
              onChange={(e) => setNewOvEnd(e.target.value)}
              className="w-full px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#548B2E] text-sm"
            />
          </div>
          <div className="sm:col-span-3">
            <label className="block text-xs font-medium text-gray-600 mb-1">Available slots (none selected = no delivery)</label>
            {renderSlotMultiSelect(newOvSlotIds, setNewOvSlotIds)}
          </div>
          <div className="flex items-end">
            <button
              onClick={handleAddOverride}
              disabled={addingOv}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-[#548B2E] text-white rounded-lg hover:bg-[#3f6b20] transition-colors disabled:opacity-50 text-sm"
            >
              {addingOv ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Add
            </button>
          </div>
          <div className="sm:col-span-6">
            <label className="block text-xs font-medium text-gray-600 mb-1">Note (optional)</label>
            <input
              type="text"
              value={newOvNote}
              onChange={(e) => setNewOvNote(e.target.value)}
              placeholder="e.g. Diwali — evening only"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#548B2E] text-sm"
            />
          </div>
        </div>

        {/* Override list */}
        {loading ? null : overrides.length === 0 ? (
          <div className="p-6 text-center bg-gray-50 rounded-lg">
            <Calendar className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500 text-sm">No date overrides. Default slots are used every day.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {overrides.map((ov) => {
              const isEditing = editingOvId === ov.id;
              const slotIds = Array.isArray(ov.slotIds) ? ov.slotIds : [];
              const selectedSlots = slots.filter(s => slotIds.includes(s.id));
              return (
                <div key={ov.id} className="border border-gray-200 rounded-lg p-3">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="flex-1">
                      {isEditing ? (
                        <div className="grid grid-cols-2 gap-3 mb-3">
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Start</label>
                            <input type="date" value={editOvStart} onChange={(e) => setEditOvStart(e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded text-sm" />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">End</label>
                            <input type="date" value={editOvEnd} onChange={(e) => setEditOvEnd(e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded text-sm" />
                          </div>
                          <div className="col-span-2">
                            <label className="block text-xs text-gray-500 mb-1">Available slots</label>
                            {renderSlotMultiSelect(editOvSlotIds, setEditOvSlotIds)}
                          </div>
                          <div className="col-span-2">
                            <label className="block text-xs text-gray-500 mb-1">Note</label>
                            <input type="text" value={editOvNote} onChange={(e) => setEditOvNote(e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded text-sm" />
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
                            <Calendar className="w-4 h-4 text-gray-500" />
                            {ov.startDate === ov.endDate ? ov.startDate : `${ov.startDate} → ${ov.endDate}`}
                          </div>
                          {ov.note && <p className="text-xs text-gray-500 mt-1 ml-6">{ov.note}</p>}
                          <div className="mt-2 ml-6 flex flex-wrap gap-1.5">
                            {selectedSlots.length === 0 ? (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700">No delivery</span>
                            ) : (
                              selectedSlots.map(s => (
                                <span key={s.id} className="text-xs px-2 py-0.5 rounded-full bg-[#548B2E]/10 text-[#548B2E]">
                                  {s.label} {formatTimeAmPm(toHHMM(s.startTime))}–{formatTimeAmPm(toHHMM(s.endTime))}
                                </span>
                              ))
                            )}
                          </div>
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {isEditing ? (
                        <>
                          <button
                            onClick={() => handleSaveOverride(ov.id)}
                            disabled={savingOvId === ov.id}
                            className="p-1.5 hover:bg-green-50 rounded-lg transition-colors"
                            title="Save"
                          >
                            {savingOvId === ov.id ? <Loader2 className="w-4 h-4 animate-spin text-green-600" /> : <Save className="w-4 h-4 text-green-600" />}
                          </button>
                          <button
                            onClick={cancelEditOverride}
                            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Cancel"
                          >
                            <X className="w-4 h-4 text-gray-500" />
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => startEditOverride(ov)}
                          className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4 text-gray-500" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteOverride(ov)}
                        disabled={deletingOvId === ov.id}
                        className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        {deletingOvId === ov.id ? (
                          <Loader2 className="w-4 h-4 animate-spin text-red-400" />
                        ) : (
                          <Trash2 className="w-4 h-4 text-red-400 hover:text-red-600" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
        <p className="text-sm text-amber-800">
          <strong>How it works:</strong> Customers see all <em>active</em> default slots at checkout for scheduled delivery.
          A slot stops accepting orders once its <em>cutoff lead</em> is reached — that many hours before the slot's start
          time, which can fall on the previous day (e.g. a 5am slot with a 6h lead closes at 11pm the night before).
        </p>
        <p className="text-sm text-amber-800 mt-2">
          <strong>Date overrides:</strong> For a date covered by an override, customers see only the slots selected in that override
          (an empty selection blocks delivery for those dates entirely). If two overrides overlap, the most recently created one wins.
        </p>
      </div>
    </div>
  );
};

export default DeliverySlotsSettings;
